import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLectura } from '../context/LecturaContext';

// Comprime la imagen a máx 1200px y JPEG 0.75 — garantiza < 300 KB
function comprimirImagen(file) {
  return new Promise((resolve) => {
    const MAX_PX = 1200;
    const QUALITY = 0.75;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_PX || height > MAX_PX) {
          if (width > height) { height = Math.round(height * MAX_PX / width); width = MAX_PX; }
          else                { width = Math.round(width * MAX_PX / height);  height = MAX_PX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(new File([blob], 'mano.jpg', { type: 'image/jpeg' }));
        }, 'image/jpeg', QUALITY);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const ICONOS_TIPO = { tierra: '🌍', agua: '💧', aire: '💨', fuego: '🔥' };
const NOMBRES_TIPO = { tierra: 'Tierra', agua: 'Agua', aire: 'Aire', fuego: 'Fuego' };

function Formulario() {
  const navigate = useNavigate();
  const { guardarPendingForm } = useLectura();
  const [formData, setFormData] = useState({
    nombre: '',
    intencion: '',
    mano: '',
    foto: null
  });
  const [preview, setPreview]             = useState(null);
  const [pesoKB, setPesoKB]               = useState(null);
  const [tipoManoDetectado, setTipoManoDetectado] = useState(null);
  const [ladoManoDetectado, setLadoManoDetectado] = useState(null);
  const [analizandoMano, setAnalizandoMano]       = useState(false);
  const [error, setError]                         = useState('');

  const inputGaleriaRef = useRef(null);
  const inputCamaraRef  = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const procesarFoto = async (file) => {
    if (!file) return;
    const comprimida = await comprimirImagen(file);
    const objectUrl  = URL.createObjectURL(comprimida);
    setFormData(prev => ({ ...prev, foto: comprimida }));
    setPreview(objectUrl);
    setPesoKB(Math.round(comprimida.size / 1024));
    setTipoManoDetectado(null);
    setLadoManoDetectado(null);
    setAnalizandoMano(true);
    try {
      const { analizarManoConMediaPipe } = await import('../utils/mediapipeService');
      const res = await analizarManoConMediaPipe(objectUrl);
      if (res.exito) {
        setTipoManoDetectado(res.tipoMano);
        if (res.ladoMano) {
          setLadoManoDetectado(res.ladoMano);
          // Auto-sugerir diestra/no_diestra solo si el usuario no ha elegido aún
          setFormData(prev => {
            if (prev.mano) return prev; // ya eligió, no sobrescribir
            return { ...prev, mano: res.ladoMano === 'derecha' ? 'diestra' : 'no_diestra' };
          });
        }
      }
    } catch (e) {
      // MediaPipe no disponible: el servidor usará Rekognition como respaldo
      console.warn('MediaPipe no disponible:', e.message);
    } finally {
      setAnalizandoMano(false);
    }
  };

  const handleFoto   = (e) => procesarFoto(e.target.files[0]);
  const handleCamara = (e) => procesarFoto(e.target.files[0]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.mano) {
      setError('Indica si es tu mano diestra o no diestra.');
      return;
    }
    if (!formData.foto) {
      setError('Por favor toma o elige una foto de tu mano.');
      return;
    }

    // Guardar datos del form en el contexto y navegar INMEDIATAMENTE
    // La llamada a la API ocurre en Procesando.js mientras el usuario
    // ve la animación mística (no espera aquí en el formulario)
    guardarPendingForm({
      nombre:          formData.nombre,
      intencion:       formData.intencion,
      mano:            formData.mano,
      foto:            formData.foto,
      tipoManoCliente: tipoManoDetectado || undefined,
      ladoManoCliente: ladoManoDetectado || undefined
    });
    navigate('/procesando');
  };

  return (
    <div className="contenedor">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="titulo">Tu lectura te espera</h2>
        
        <form onSubmit={handleSubmit} style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-oro)' }}>
              Tu nombre *
            </label>
            <input
              type="text"
              name="nombre"
              className="input"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="María"
              required
              maxLength="50"
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--color-oro)' }}>
              ¿Qué deseas conocer? *
            </label>
            <select
              name="intencion"
              className="input"
              value={formData.intencion}
              onChange={handleChange}
              required
              style={{ cursor: 'pointer' }}
            >
              <option value="">Selecciona...</option>
              <option value="amor">❤️ Amor y relaciones</option>
              <option value="salud">💚 Salud y bienestar</option>
              <option value="trabajo">💼 Trabajo y éxito</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.8rem', color: 'var(--color-oro)' }}>
              ¿Qué mano vas a mostrar? *
            </label>
            <div style={{ display: 'flex', gap: '0.8rem' }}>
              {[
                { valor: 'diestra',    emoji: '✍️', label: 'Mano diestra',    sub: 'Con la que escribes' },
                { valor: 'no_diestra', emoji: '🤚', label: 'Mano no diestra', sub: 'Tu mano del alma' }
              ].map(({ valor, emoji, label, sub }) => (
                <button
                  key={valor}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, mano: valor }))}
                  style={{
                    flex: 1,
                    padding: '1rem 0.5rem',
                    background: formData.mano === valor ? 'rgba(255,215,0,0.12)' : 'var(--color-secundario)',
                    border: `2px solid ${formData.mano === valor ? 'var(--color-oro)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '12px',
                    color: 'var(--color-texto)',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '1.8rem' }}>{emoji}</div>
                  <div style={{ fontWeight: '600', fontSize: '0.9rem', marginTop: '0.3rem' }}>{label}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-texto-suave)', marginTop: '0.2rem' }}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.8rem', color: 'var(--color-oro)' }}>
              Foto de tu mano *
            </label>

            {/* Inputs ocultos */}
            <input
              ref={inputGaleriaRef}
              type="file"
              accept="image/*"
              onChange={handleFoto}
              style={{ display: 'none' }}
            />
            <input
              ref={inputCamaraRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCamara}
              style={{ display: 'none' }}
            />

            {/* Botones visibles */}
            {!preview ? (
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={() => inputCamaraRef.current.click()}
                  style={{
                    flex: 1,
                    padding: '1.2rem',
                    background: 'var(--color-secundario)',
                    border: '2px dashed var(--color-acento)',
                    borderRadius: '12px',
                    color: 'var(--color-texto)',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.4rem',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <span style={{ fontSize: '2rem' }}>📷</span>
                  <span>Tomar foto</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-texto-suave)' }}>Cámara</span>
                </button>

                <button
                  type="button"
                  onClick={() => inputGaleriaRef.current.click()}
                  style={{
                    flex: 1,
                    padding: '1.2rem',
                    background: 'var(--color-secundario)',
                    border: '2px dashed rgba(255,255,255,0.15)',
                    borderRadius: '12px',
                    color: 'var(--color-texto)',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.4rem',
                    transition: 'border-color 0.2s'
                  }}
                >
                  <span style={{ fontSize: '2rem' }}>🖼️</span>
                  <span>Elegir foto</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-texto-suave)' }}>Galería</span>
                </button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ position: 'relative', display: 'inline-block', width: '100%' }}
              >
                <img
                  src={preview}
                  alt="Tu mano"
                  style={{
                    width: '100%',
                    maxHeight: '260px',
                    objectFit: 'cover',
                    borderRadius: '12px',
                    border: '2px solid var(--color-acento)',
                    display: 'block'
                  }}
                />
                {/* Badge de peso */}
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(0,0,0,0.7)',
                  color: 'var(--color-oro)',
                  fontSize: '0.75rem',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '20px'
                }}>
                  ✅ {pesoKB} KB
                </span>

                {/* Badge tipo de mano (MediaPipe) */}
                {analizandoMano && (
                  <div style={{ marginTop: '0.6rem', textAlign: 'center', color: 'var(--color-oro)', fontSize: '0.82rem' }}>
                    🔭 Selene observa tu mano…
                  </div>
                )}
                {tipoManoDetectado && !analizandoMano && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      marginTop: '0.6rem',
                      textAlign: 'center',
                      padding: '0.4rem 0.8rem',
                      background: 'rgba(255,215,0,0.1)',
                      border: '1px solid rgba(255,215,0,0.35)',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      color: 'var(--color-oro)'
                    }}
                  >
                    {ICONOS_TIPO[tipoManoDetectado]} Mano de {NOMBRES_TIPO[tipoManoDetectado]} detectada
                    {ladoManoDetectado && (
                      <span style={{ marginLeft: '0.5rem', opacity: 0.85 }}>
                        • {ladoManoDetectado === 'derecha' ? '✍️ derecha' : '🤚 izquierda'}
                      </span>
                    )}
                  </motion.div>
                )}
                {/* Botón cambiar */}
                <button
                  type="button"
                  onClick={() => { setPreview(null); setPesoKB(null); setTipoManoDetectado(null); setLadoManoDetectado(null); setFormData(p => ({ ...p, foto: null })); }}
                  style={{
                    marginTop: '0.6rem',
                    width: '100%',
                    padding: '0.5rem',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'var(--color-texto-suave)',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  ↩ Cambiar foto
                </button>
              </motion.div>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ 
                color: 'var(--color-acento)', 
                marginBottom: '1rem',
                padding: '1rem',
                background: 'rgba(233, 69, 96, 0.1)',
                borderRadius: '8px'
              }}
            >
              {error}
            </motion.div>
          )}

          <button
            type="submit"
            className="boton"
            style={{ width: '100%' }}
          >
            Ver lo que dice mi mano →
          </button>

          {/* AD-4: debajo del botón submit, antes de enviar */}
          <div id="ezoic-pub-ad-placeholder-104" style={{ marginTop: '1.5rem', minHeight: 0 }} />
        </form>
      </motion.div>
    </div>
  );
}

export default Formulario;